import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Application from 'expo-application';
import * as StoreReview from 'expo-store-review';
import { Platform } from 'react-native';

import { askedForPushThisSession } from './pushPrompt';

/**
 * In-app rating prompt — asks for a review at a moment the player is happy.
 *
 * The App Store product page is the landing page for every ad tap, and a page
 * with no ratings converts badly, so this is upstream of any paid acquisition.
 *
 * Apple silently caps `requestReview` at three prompts per user per 365 days
 * and ignores calls beyond that — the dialog just never appears, with no error.
 * Burning those three attempts on players who are mid-frustration wastes the
 * whole year's budget, so the gates below are deliberately conservative:
 *
 *   - only after a genuinely good session (the caller supplies the quality bar)
 *   - only once the player has real history with the app, not on first contact
 *   - at most once per app version, and never twice inside 90 days
 *   - never more than three times in the app's lifetime on this device
 *
 * Every failure path is swallowed. A rating prompt is a nicety; it must never
 * be able to take down the results screen that earned it.
 */

const COMPLETED_KEY = 'app_review_quizzes_completed';
const COUNT_KEY = 'app_review_prompt_count';
const LAST_AT_KEY = 'app_review_last_prompt_at';
const LAST_VERSION_KEY = 'app_review_last_prompt_version';

/** Apple's own ceiling. Tracked locally so we can stop asking before we hit it. */
const MAX_LIFETIME_PROMPTS = 3;
/** Quiet period between prompts, well inside Apple's rolling-year window. */
const MIN_DAYS_BETWEEN_PROMPTS = 90;
/** Sessions the player must finish before we ask for anything. */
const MIN_QUIZZES_BEFORE_PROMPT = 3;
/** Solo score, as a percentage, that counts as a session worth asking on. */
const MIN_ACCURACY_FOR_PROMPT = 70;

const DAY_MS = 24 * 60 * 60 * 1000;

function readInt(value: string | null): number {
  const parsed = parseInt(value ?? '', 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Record a finished session and, if every gate passes, ask for a store review.
 *
 * Shared by both game modes so the completion counter spans them: three
 * sessions is three sessions, whether the player got there solo, in a match,
 * or in some mix of the two.
 *
 * @param earned Whether this particular session was a high point worth asking
 *   on. Each mode decides this for itself — see the wrappers below.
 * @returns true when a prompt was actually requested from the OS.
 */
async function requestReviewIfEarned(earned: boolean): Promise<boolean> {
  try {
    // Native stores only — there is nothing to rate on web.
    if (Platform.OS !== 'ios' && Platform.OS !== 'android') return false;
    // Never nag during development; it also can't render on a plain simulator.
    if (__DEV__) return false;

    // Count the session first, so progress accrues even when we decline to ask.
    const completed = readInt(await AsyncStorage.getItem(COMPLETED_KEY)) + 1;
    await AsyncStorage.setItem(COMPLETED_KEY, String(completed));

    if (!earned) return false;
    if (completed < MIN_QUIZZES_BEFORE_PROMPT) return false;
    // Two system dialogs back to back feel like an interrogation, and the push dialog is
    // the one that can only be shown once per install — so it wins this session.
    if (askedForPushThisSession()) return false;

    const promptCount = readInt(await AsyncStorage.getItem(COUNT_KEY));
    if (promptCount >= MAX_LIFETIME_PROMPTS) return false;

    const lastAt = readInt(await AsyncStorage.getItem(LAST_AT_KEY));
    if (lastAt && Date.now() - lastAt < MIN_DAYS_BETWEEN_PROMPTS * DAY_MS) return false;

    // Apple shows the dialog at most once per version anyway; skipping here
    // keeps our own counter honest rather than spending an attempt on a no-op.
    const version = Application.nativeApplicationVersion ?? '';
    const lastVersion = await AsyncStorage.getItem(LAST_VERSION_KEY);
    if (version && version === lastVersion) return false;

    // hasAction() reports whether the OS can actually present the dialog.
    if (!(await StoreReview.isAvailableAsync())) return false;
    if (!(await StoreReview.hasAction())) return false;

    await StoreReview.requestReview();

    // The OS never tells us whether the player saw or acted on the dialog, so
    // count the attempt. Erring toward asking less is the safe direction.
    await AsyncStorage.multiSet([
      [COUNT_KEY, String(promptCount + 1)],
      [LAST_AT_KEY, String(Date.now())],
      [LAST_VERSION_KEY, version],
    ]);

    return true;
  } catch (error) {
    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.warn('[appReview] requestReviewIfEarned failed:', error);
    }
    return false;
  }
}

/** Streak milestones, in days, that count as a high point worth asking on. */
const STREAK_MILESTONES = [7, 30, 100];

/**
 * Solo play: ask after a session the player did well in, OR after they just
 * hit a streak milestone.
 *
 * These are two different good feelings, not the same one measured two ways.
 * Accuracy is a snapshot of one session; a streak is momentum built over many.
 * A player who just broke their personal record for showing up is exactly who
 * a five-star ask should reach, even on a session where they missed a few
 * questions — so this checks "just hit 7 days" as its own qualifying reason
 * rather than requiring both to be true at once.
 *
 * @param accuracy Percentage score for the session just finished (0-100).
 * @param streakDays Current daily streak in days, if known. Pass the value
 *   *after* this session's completion is recorded, so a streak that just
 *   became 7 reads as 7, not 6.
 */
export function maybeRequestReviewAfterQuiz(
  accuracy: number,
  streakDays?: number
): Promise<boolean> {
  const goodSession = accuracy >= MIN_ACCURACY_FOR_PROMPT;
  const justHitMilestone = typeof streakDays === 'number' && STREAK_MILESTONES.includes(streakDays);
  return requestReviewIfEarned(goodSession || justHitMilestone);
}

/**
 * Multiplayer: ask only after a win or a tie.
 *
 * Accuracy is the wrong bar here — you can take a match with a mediocre score
 * or lose one with a good score, and it's the outcome the player feels. Placing
 * second is a fine result but not a five-star feeling, and with only three
 * prompts a year to spend we keep them for unambiguous highs.
 *
 * A "win" in a room the player was alone in isn't one, hence the field check.
 */
export function maybeRequestReviewAfterMatch(params: {
  isWinner: boolean;
  isTie: boolean;
  totalPlayers: number;
}): Promise<boolean> {
  const { isWinner, isTie, totalPlayers } = params;
  return requestReviewIfEarned(totalPlayers >= 2 && (isWinner || isTie));
}
